import {
  Bot,
  Check,
  FileText,
  Image as ImageIcon,
  LoaderCircle,
  MessageCircle,
  PanelRightClose,
  PanelRightOpen,
  Plus,
  Paperclip,
  Send,
  Sparkles,
  X,
} from "lucide-react";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createJustinianoClient } from "./client.js";
import { CitationCard } from "./CitationCard.jsx";
import { normalizeRestoredMessages } from "./history.js";
import {
  JUSTINIANO_LAYOUT_EVENT,
  JUSTINIANO_LAYOUT_STORAGE_KEY,
  persistJustinianoLayoutPreference,
  readJustinianoLayoutPreference,
} from "./layout-preference.js";
import "./styles.css";

const AssistantContent = lazy(() => import("./AssistantContent.jsx"));

let localMessageSequence = 0;
const acceptedAttachmentExtensions = new Set([
  "csv",
  "doc",
  "docx",
  "gif",
  "jpeg",
  "jpg",
  "md",
  "odt",
  "pdf",
  "png",
  "ppt",
  "pptx",
  "rtf",
  "tsv",
  "txt",
  "webp",
  "xls",
  "xlsx",
]);
const maximumAttachmentBytes = 10 * 1024 * 1024;
const dockBreakpoint = 1025;
const resizeBreakpoint = 621;
let dockOwnerSequence = 0;

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

function panelWidthForViewport(width, viewportWidth) {
  const maximum = Math.max(1, Math.floor(viewportWidth * 0.5));
  const minimum = Math.min(320, maximum);
  return Math.round(clamp(Number(width) || 408, minimum, maximum));
}

function panelHeightForViewport(height, viewportHeight) {
  const maximum = Math.max(1, Math.floor(viewportHeight - 110));
  const minimum = Math.min(480, maximum);
  return Math.round(clamp(Number(height) || 680, minimum, maximum));
}

function localId(prefix) {
  localMessageSequence += 1;
  return `${prefix}-${Date.now()}-${localMessageSequence}`;
}

function AssistantMark({ size = 20 }) {
  return (
    <span className="justiniano-mark" aria-hidden="true">
      <Sparkles size={size} strokeWidth={2.2} />
    </span>
  );
}

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentChip({ attachment, removable = false, onRemove }) {
  const Icon = attachment.kind === "image" ? ImageIcon : FileText;
  return (
    <span className="justiniano-attachment-chip">
      <Icon size={13} />
      <span>
        <strong>{attachment.fileName}</strong>
        {attachment.size ? <small>{formatFileSize(attachment.size)}</small> : null}
      </span>
      {removable && (
        <button
          type="button"
          onClick={() => onRemove(attachment)}
          aria-label={`Quitar ${attachment.fileName}`}
          title="Quitar archivo"
        >
          <X size={12} />
        </button>
      )}
    </span>
  );
}

function ToolResult({ result, renderers, context }) {
  if (!result) return null;
  const rendered = renderers?.renderToolResult?.({
    toolName: result.name,
    data: result.data,
    context,
  });
  if (rendered !== undefined && rendered !== null) {
    return <div className="justiniano-tool-result">{rendered}</div>;
  }
  return null;
}

function Sources({ citations, renderers, context }) {
  if (!citations?.length) return null;

  return (
    <section
      className="justiniano-sources"
      aria-label="Fuentes y referencias de la respuesta"
    >
      <div className="justiniano-sources-title">
        Fuentes y referencias
        <span>{citations.length}</span>
      </div>
      <div className="justiniano-sources-list">
        {citations.map((citation, index) => {
          const rendered = renderers?.renderCitation?.({
            citation,
            index,
            context,
          });
          return (
            <div
              key={citation.evidenceId || citation.id || `${index}-${citation.label}`}
            >
              {rendered || <CitationCard citation={citation} index={index} />}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Confirmation({
  action,
  busy,
  renderers,
  context,
  onConfirm,
  onCancel,
}) {
  const rendered = renderers?.renderConfirmation?.({
    action,
    context,
  });

  return (
    <div className={`justiniano-confirmation is-${action.status}`}>
      {rendered || (
        <p>
          Confirmá si querés que Justiniano ejecute esta acción en el espacio
          seleccionado.
        </p>
      )}
      {action.status === "pending" && (
        <div className="justiniano-confirmation-actions">
          <button
            type="button"
            className="justiniano-secondary-button"
            onClick={onCancel}
            disabled={busy}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="justiniano-primary-button"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? <LoaderCircle className="is-spinning" size={15} /> : <Check size={15} />}
            Confirmar
          </button>
        </div>
      )}
      {action.status === "completed" && (
        <span className="justiniano-action-state">
          <Check size={14} /> Acción realizada
        </span>
      )}
      {action.status === "cancelled" && (
        <span className="justiniano-action-state">Acción cancelada</span>
      )}
      {action.status === "executing" && (
        <span className="justiniano-action-state">
          <LoaderCircle className="is-spinning" size={14} /> Acción en curso
        </span>
      )}
      {action.status === "failed" && (
        <span className="justiniano-action-state is-error">
          No se pudo completar la acción
        </span>
      )}
    </div>
  );
}

function ConversationMessage({
  message,
  renderers,
  context,
  onAction,
  onConfirm,
  onCancel,
}) {
  const isAssistant = message.role === "assistant";

  return (
    <article
      className={`justiniano-message is-${message.role} ${
        message.status === "error" ? "has-error" : ""
      }`}
    >
      {isAssistant && (
        <span className="justiniano-message-avatar">
          <Bot size={15} />
        </span>
      )}
      <div className="justiniano-message-content">
        {!isAssistant && message.attachments?.length > 0 && (
          <div className="justiniano-message-attachments">
            {message.attachments.map((attachment) => (
              <AttachmentChip
                key={attachment.id}
                attachment={attachment}
              />
            ))}
          </div>
        )}
        {message.content && (
          <div className="justiniano-bubble">
            {isAssistant ? (
              <Suspense fallback={message.content}>
                <AssistantContent onAction={onAction}>
                  {message.content}
                </AssistantContent>
              </Suspense>
            ) : (
              message.content
            )}
          </div>
        )}
        {message.status === "thinking" && !message.content && (
          <div className="justiniano-thinking" aria-label="Justiniano está pensando">
            <span />
            <span />
            <span />
          </div>
        )}
        {isAssistant && message.statusText && (
          <div className="justiniano-status" role="status" aria-live="polite">
            <LoaderCircle className="is-spinning" size={12} />
            <span>{message.statusText}</span>
          </div>
        )}
        <ToolResult
          result={message.toolResult}
          renderers={renderers}
          context={context}
        />
        {message.confirmation && (
          <Confirmation
            action={message.confirmation}
            busy={message.confirmationBusy}
            renderers={renderers}
            context={context}
            onConfirm={() => onConfirm(message.id, message.confirmation)}
            onCancel={() => onCancel(message.id, message.confirmation)}
          />
        )}
        {message.warning && (
          <p className="justiniano-warning">{message.warning}</p>
        )}
        <Sources
          citations={message.citations}
          renderers={renderers}
          context={context}
        />
        {isAssistant && message.generatedContent && (
          <span className="justiniano-ai-label">
            Explicación generada con IA
          </span>
        )}
      </div>
    </article>
  );
}

export function Justiniano({
  appId,
  appName,
  apiUrl,
  getAccessToken,
  variant = "floating",
  welcome = "Hola, soy Justiniano. ¿En qué puedo ayudarte?",
  suggestions = [],
  renderers,
  context,
  appContext,
  allowAttachments = false,
  notebookIds = [],
  notebookScope = "selected",
  enablePageHelp = false,
  onNavigate,
}) {
  const pageVariant = variant === "page";
  const [open, setOpen] = useState(pageVariant);
  const visible = pageVariant || open;
  const [layoutPreference, setLayoutPreference] = useState(() => (
    readJustinianoLayoutPreference()
  ));
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === "undefined" ? 1440 : window.innerWidth,
    height: typeof window === "undefined" ? 900 : window.innerHeight,
  }));
  const [resizing, setResizing] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");
  const [draggingFiles, setDraggingFiles] = useState(false);
  const [pageHelp, setPageHelp] = useState(null);
  const [knownActions, setKnownActions] = useState({});
  const [proactiveTip, setProactiveTip] = useState(null);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const abortRef = useRef(null);
  const restoreStartedRef = useRef(false);
  const instantScrollRef = useRef(true);
  const previousVisibleRef = useRef(visible);
  const tipTimerRef = useRef(null);
  const resizeRef = useRef(null);
  const layoutPreferenceRef = useRef(layoutPreference);
  const dockOwnerRef = useRef(null);
  if (!dockOwnerRef.current) {
    dockOwnerSequence += 1;
    dockOwnerRef.current = `justiniano-dock-${dockOwnerSequence}`;
  }

  const canDock = !pageVariant && viewport.width >= dockBreakpoint;
  const canResize = !pageVariant && viewport.width >= resizeBreakpoint;
  const docked = canDock && layoutPreference.docked;
  const panelWidth = panelWidthForViewport(
    layoutPreference.width,
    viewport.width,
  );
  const floatingHeight = panelHeightForViewport(
    layoutPreference.floatingHeight,
    viewport.height,
  );
  const dockActive = visible && docked;

  layoutPreferenceRef.current = layoutPreference;

  const commitLayoutPreference = useCallback((layout) => {
    const next = persistJustinianoLayoutPreference(layout);
    layoutPreferenceRef.current = next;
    setLayoutPreference(next);
  }, []);

  const client = useMemo(
    () =>
      createJustinianoClient({
        apiUrl,
        appId,
        getAccessToken,
        surface: pageVariant
          ? "full_page_assistant"
          : "floating_assistant",
      }),
    [apiUrl, appId, getAccessToken, pageVariant],
  );

  const updateMessage = useCallback((id, updater) => {
    setMessages((current) =>
      current.map((message) =>
        message.id === id ? updater(message) : message,
      ),
    );
  }, []);

  useEffect(() => {
    if (pageVariant) return undefined;
    const updateViewport = () => setViewport({
      width: window.innerWidth,
      height: window.innerHeight,
    });
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, [pageVariant]);

  useEffect(() => {
    if (pageVariant) return undefined;
    const sync = (event) => {
      if (event?.type === "storage" && event.key !== JUSTINIANO_LAYOUT_STORAGE_KEY) {
        return;
      }
      const next = readJustinianoLayoutPreference();
      layoutPreferenceRef.current = next;
      setLayoutPreference(next);
    };
    const syncWhenVisible = () => {
      if (document.visibilityState === "visible") sync();
    };
    window.addEventListener(JUSTINIANO_LAYOUT_EVENT, sync);
    window.addEventListener("focus", sync);
    window.addEventListener("pageshow", sync);
    window.addEventListener("storage", sync);
    document.addEventListener("visibilitychange", syncWhenVisible);
    return () => {
      window.removeEventListener(JUSTINIANO_LAYOUT_EVENT, sync);
      window.removeEventListener("focus", sync);
      window.removeEventListener("pageshow", sync);
      window.removeEventListener("storage", sync);
      document.removeEventListener("visibilitychange", syncWhenVisible);
    };
  }, [pageVariant]);

  useEffect(() => {
    if (!dockActive) return undefined;
    const root = document.documentElement;
    const owner = dockOwnerRef.current;
    root.dataset.justinianoDockOwner = owner;
    root.classList.add("justiniano-dock-active");
    return () => {
      if (root.dataset.justinianoDockOwner !== owner) return;
      root.classList.remove("justiniano-dock-active");
      root.style.removeProperty("--justiniano-dock-width");
      delete root.dataset.justinianoDockOwner;
    };
  }, [dockActive]);

  useEffect(() => {
    if (!dockActive) return;
    const root = document.documentElement;
    if (root.dataset.justinianoDockOwner !== dockOwnerRef.current) return;
    root.style.setProperty("--justiniano-dock-width", `${panelWidth}px`);
  }, [dockActive, panelWidth]);

  useEffect(() => {
    if (pageVariant) return undefined;
    const resizePanel = (event) => {
      const operation = resizeRef.current;
      if (!operation) return;
      let width = operation.width;
      let floatingPanelHeight = operation.height;
      if (operation.mode === "width" || operation.mode === "both") {
        width = panelWidthForViewport(
          operation.startWidth + operation.startX - event.clientX,
          window.innerWidth,
        );
      }
      if (operation.mode === "height" || operation.mode === "both") {
        floatingPanelHeight = panelHeightForViewport(
          operation.startHeight + operation.startY - event.clientY,
          window.innerHeight,
        );
      }
      operation.width = width;
      operation.height = floatingPanelHeight;
      const next = {
        ...layoutPreferenceRef.current,
        width,
        floatingHeight: floatingPanelHeight,
      };
      layoutPreferenceRef.current = next;
      setLayoutPreference(next);
    };
    const finishResize = () => {
      const operation = resizeRef.current;
      if (!operation) return;
      resizeRef.current = null;
      setResizing(false);
      document.documentElement.classList.remove("justiniano-resizing");
      commitLayoutPreference({
        ...layoutPreferenceRef.current,
        width: operation.width,
        floatingHeight: operation.height,
      });
    };
    window.addEventListener("pointermove", resizePanel);
    window.addEventListener("pointerup", finishResize);
    window.addEventListener("pointercancel", finishResize);
    return () => {
      window.removeEventListener("pointermove", resizePanel);
      window.removeEventListener("pointerup", finishResize);
      window.removeEventListener("pointercancel", finishResize);
      document.documentElement.classList.remove("justiniano-resizing");
    };
  }, [commitLayoutPreference, pageVariant]);

  useEffect(() => {
    if (!visible || restoreStartedRef.current) return;
    restoreStartedRef.current = true;
    setRestoring(true);
    setRestoreError("");

    Promise.resolve()
      .then(() => client.listConversations())
      .then(async (conversations) => {
        const latest = conversations?.[0];
        if (!latest) return;
        const restored = await client.loadConversation(latest.id);
        setConversationId(restored.conversation.id);
        setAttachments(restored.attachments || []);
        instantScrollRef.current = true;
        setMessages(
          normalizeRestoredMessages(
            restored.messages,
            restored.toolActions,
          ),
        );
      })
      .catch((error) => {
        setRestoreError(
          error.message || "No pude recuperar la conversación anterior.",
        );
      })
      .finally(() => {
        setRestoring(false);
      });
  }, [client, visible]);

  useEffect(() => {
    if (!enablePageHelp) return undefined;
    let active = true;
    window.clearTimeout(tipTimerRef.current);
    setProactiveTip(null);
    client.getPageHelp(appContext?.route || "/")
      .then((help) => {
        if (!active) return;
        setPageHelp(help);
        if (!help) return;
        setKnownActions((current) => ({
          ...current,
          ...Object.fromEntries(
            (help.targets || []).map((action) => [action.id, action]),
          ),
        }));
        if (pageVariant || !help.tip) return;
        const storageKey = [
          "justiniano-tip",
          appId,
          help.tip.id,
          help.tip.version,
        ].join(":");
        let seen = false;
        try {
          seen = window.localStorage.getItem(storageKey) === "seen";
        } catch {
          seen = false;
        }
        if (!seen) {
          tipTimerRef.current = window.setTimeout(() => {
            if (active) setProactiveTip({ ...help.tip, storageKey });
          }, 700);
        }
      })
      .catch(() => {
        if (active) setPageHelp(null);
      });
    return () => {
      active = false;
      window.clearTimeout(tipTimerRef.current);
    };
  }, [
    appContext?.route,
    appId,
    client,
    enablePageHelp,
    pageVariant,
  ]);

  useEffect(() => {
    if (!visible) return undefined;
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 100);
    const onKeyDown = (event) => {
      if (!pageVariant && event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [pageVariant, visible]);

  useEffect(() => {
    const compose = (event) => {
      const detail = event.detail || {};
      if (detail.appId && detail.appId !== appId) return;
      const prompt = String(detail.prompt || "").trim();
      if (!prompt) return;
      if (!pageVariant) setOpen(true);
      setDraft(prompt);
    };
    window.addEventListener("justiniano:compose", compose);
    return () => window.removeEventListener("justiniano:compose", compose);
  }, [appId, pageVariant]);

  useLayoutEffect(() => {
    const justOpened = visible && !previousVisibleRef.current;
    previousVisibleRef.current = visible;
    if (!visible || !scrollRef.current) return;

    if (justOpened || instantScrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      instantScrollRef.current = false;
      return;
    }

    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, visible]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  async function ensureConversation() {
    if (conversationId) return conversationId;
    const conversation = await client.createConversation();
    setConversationId(conversation.id);
    return conversation.id;
  }

  async function send(content = draft) {
    const normalized = String(content || "").trim();
    if (
      !normalized
      || sending
      || restoring
      || uploadingAttachment
    ) return;

    const userMessage = {
      id: localId("user"),
      role: "user",
      status: "completed",
      content: normalized,
      attachments: [...attachments],
    };
    const assistantId = localId("assistant");
    setMessages((current) => [
      ...current,
      userMessage,
      {
        id: assistantId,
        role: "assistant",
        status: "thinking",
        content: "",
        citations: [],
        statusText: "Iniciando consulta…",
      },
    ]);
    setDraft("");
    setSending(true);

    try {
      const id = await ensureConversation();
      const controller = new AbortController();
      abortRef.current = controller;

      await client.sendMessage(
        id,
        normalized,
        (event, data) => {
          updateMessage(assistantId, (message) => {
            if (event === "status") {
              return {
                ...message,
                status: "thinking",
                statusText: data.message || "Procesando la consulta…",
                statusStage: data.stage || null,
              };
            }
            if (event === "answer.delta") {
              return {
                ...message,
                status: "streaming",
                content: `${message.content}${data.delta || ""}`,
                statusText: null,
              };
            }
            if (event === "tool.started") {
              const customStatus = renderers?.getToolStatus?.({
                toolName: data.name,
                context,
              });
              return {
                ...message,
                status: "thinking",
                activeTool: data.name,
                statusText:
                  customStatus || `Consultando información de ${appName}…`,
              };
            }
            if (event === "tool.completed") {
              return { ...message, toolResult: data };
            }
            if (event === "confirmation.required") {
              return {
                ...message,
                confirmation: { ...data, status: "pending" },
              };
            }
            if (event === "citation") {
              if (
                message.citations?.some(
                  (citation) =>
                    citation.evidenceId &&
                    citation.evidenceId === data.evidenceId,
                )
              ) {
                return message;
              }
              return {
                ...message,
                citations: [...(message.citations || []), data],
              };
            }
            if (event === "warning") {
              return { ...message, warning: data.message };
            }
            if (event === "completed") {
              return {
                ...message,
                status: "completed",
                serverId: data.messageId,
                statusText: null,
                generatedContent: Boolean(data.generatedContent),
                evidenceMode: data.evidenceMode || null,
              };
            }
            if (event === "error") {
              return {
                ...message,
                status: "error",
                content:
                  data.message ||
                  "No pude completar la consulta. Intentá nuevamente.",
                statusText: null,
              };
            }
            return message;
          });
        },
        {
          signal: controller.signal,
          appContext,
          attachmentIds: attachments.map((attachment) => attachment.id),
          notebookIds,
          notebookScope,
        },
      );
    } catch (error) {
      if (error.name !== "AbortError") {
        updateMessage(assistantId, (message) => ({
          ...message,
          status: "error",
          content:
            error.message || "No pude completar la consulta. Intentá nuevamente.",
          statusText: null,
        }));
      }
    } finally {
      abortRef.current = null;
      setSending(false);
    }
  }

  async function uploadFiles(fileList) {
    const files = [...(fileList || [])];
    if (!files.length || uploadingAttachment) return;
    const supportedFiles = files.filter((file) => {
      const extension = file.name.split(".").at(-1)?.toLowerCase();
      return (
        acceptedAttachmentExtensions.has(extension)
        && file.size <= maximumAttachmentBytes
      );
    });
    if (!supportedFiles.length) {
      setAttachmentError(
        "Los archivos deben tener un formato compatible y pesar hasta 10 MB.",
      );
      return;
    }
    const availableSlots = Math.max(0, 5 - attachments.length);
    if (!availableSlots) {
      setAttachmentError(
        "Podés mantener hasta 5 archivos activos por conversación.",
      );
      return;
    }
    setUploadingAttachment(true);
    setAttachmentError("");
    try {
      const id = await ensureConversation();
      const uploaded = [];
      const failures = [];
      for (const file of supportedFiles.slice(0, availableSlots)) {
        try {
          uploaded.push(await client.uploadAttachment(id, file));
        } catch (error) {
          failures.push(error.message || file.name);
        }
      }
      if (uploaded.length) {
        setAttachments((current) => {
          const byId = new Map(current.map((attachment) => [
            attachment.id,
            attachment,
          ]));
          for (const attachment of uploaded) {
            byId.set(attachment.id, attachment);
          }
          return [...byId.values()];
        });
      }
      if (failures.length) {
        setAttachmentError(
          failures.length === 1
            ? failures[0]
            : `No se pudieron adjuntar ${failures.length} archivos.`,
        );
      } else if (supportedFiles.length > availableSlots) {
        setAttachmentError(
          `Se adjuntaron ${availableSlots} archivos; el máximo activo es 5.`,
        );
      } else if (supportedFiles.length !== files.length) {
        setAttachmentError(
          "Se ignoraron archivos incompatibles o mayores a 10 MB.",
        );
      }
    } catch (error) {
      setAttachmentError(
        error.message || "No pudimos adjuntar el archivo.",
      );
    } finally {
      setUploadingAttachment(false);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }

  function beginFileDrag(event) {
    if (
      !allowAttachments
      || !Array.from(event.dataTransfer?.types || []).includes("Files")
    ) {
      return;
    }
    event.preventDefault();
    if (uploadingAttachment || attachments.length >= 5) {
      setAttachmentError(
        "Podés mantener hasta 5 archivos activos por conversación.",
      );
      return;
    }
    dragDepthRef.current += 1;
    setDraggingFiles(true);
  }

  function continueFileDrag(event) {
    if (
      !allowAttachments
      || !Array.from(event.dataTransfer?.types || []).includes("Files")
    ) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect =
      uploadingAttachment || attachments.length >= 5
        ? "none"
        : "copy";
  }

  function endFileDrag(event) {
    if (!allowAttachments) return;
    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDraggingFiles(false);
  }

  function dropFiles(event) {
    if (!allowAttachments) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setDraggingFiles(false);
    if (uploadingAttachment || attachments.length >= 5) return;
    void uploadFiles(event.dataTransfer?.files);
  }

  async function removeAttachment(attachment) {
    if (!conversationId || uploadingAttachment) return;
    setUploadingAttachment(true);
    setAttachmentError("");
    try {
      await client.deleteAttachment(conversationId, attachment.id);
      setAttachments((current) =>
        current.filter((item) => item.id !== attachment.id),
      );
    } catch (error) {
      setAttachmentError(
        error.message || "No pudimos quitar el archivo.",
      );
    } finally {
      setUploadingAttachment(false);
    }
  }

  async function confirmAction(messageId, action) {
    if (!conversationId) return;
    updateMessage(messageId, (message) => ({
      ...message,
      confirmationBusy: true,
    }));
    try {
      const result = await client.confirmAction(conversationId, action.actionId);
      updateMessage(messageId, (message) => ({
        ...message,
        confirmationBusy: false,
        confirmation: { ...message.confirmation, status: "completed" },
      }));
      setMessages((current) => [
        ...current,
        {
          id: localId("assistant"),
          role: "assistant",
          status: "completed",
          content: result.message?.content || "La acción se completó correctamente.",
          toolResult: result.action?.output
            ? { name: result.action.toolName, data: result.action.output }
            : null,
        },
      ]);
    } catch (error) {
      updateMessage(messageId, (message) => ({
        ...message,
        confirmationBusy: false,
        warning: error.message,
      }));
    }
  }

  async function cancelAction(messageId, action) {
    if (!conversationId) return;
    updateMessage(messageId, (message) => ({
      ...message,
      confirmationBusy: true,
    }));
    try {
      await client.cancelAction(conversationId, action.actionId);
      updateMessage(messageId, (message) => ({
        ...message,
        confirmationBusy: false,
        confirmation: { ...message.confirmation, status: "cancelled" },
      }));
    } catch (error) {
      updateMessage(messageId, (message) => ({
        ...message,
        confirmationBusy: false,
        warning: error.message,
      }));
    }
  }

  function newConversation() {
    abortRef.current?.abort();
    restoreStartedRef.current = true;
    instantScrollRef.current = true;
    setConversationId(null);
    setMessages([]);
    setDraft("");
    setSending(false);
    setRestoring(false);
    setRestoreError("");
    setAttachments([]);
    setAttachmentError("");
    setUploadingAttachment(false);
    setDraggingFiles(false);
    dragDepthRef.current = 0;
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  function dismissProactiveTip() {
    if (proactiveTip?.storageKey) {
      try {
        window.localStorage.setItem(proactiveTip.storageKey, "seen");
      } catch {
        // The tip remains dismissible when browser storage is unavailable.
      }
    }
    setProactiveTip(null);
  }

  function focusUiTarget(targetName) {
    if (!targetName) return;
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const target = [...document.querySelectorAll("[data-justiniano-target]")]
        .find((element) => element.dataset.justinianoTarget === targetName);
      if (!target && Date.now() - startedAt < 2_500) return;
      window.clearInterval(timer);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof target.focus === "function") {
        target.focus({ preventScroll: true });
      }
      target.classList.add("justiniano-target-highlight");
      window.setTimeout(() => {
        target.classList.remove("justiniano-target-highlight");
      }, 1_800);
    }, 60);
  }

  function runUiAction(actionId) {
    const action = knownActions[actionId];
    if (!action) return;
    dismissProactiveTip();
    if (action.route && action.route !== appContext?.route) {
      onNavigate?.(action.route);
    }
    focusUiTarget(action.target);
  }

  function beginResize(mode, event) {
    if (!canResize || event.button !== 0) return;
    const bounds = panelRef.current?.getBoundingClientRect();
    if (!bounds) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    resizeRef.current = {
      mode,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: bounds.width,
      startHeight: bounds.height,
      width: panelWidth,
      height: floatingHeight,
    };
    setResizing(true);
    document.documentElement.classList.add("justiniano-resizing");
  }

  function resizeWithKeyboard(axis, event) {
    const direction = axis === "width"
      ? { ArrowLeft: 1, ArrowRight: -1 }[event.key]
      : { ArrowUp: 1, ArrowDown: -1 }[event.key];
    if (!direction) return;
    event.preventDefault();
    const amount = event.shiftKey ? 32 : 16;
    commitLayoutPreference({
      ...layoutPreferenceRef.current,
      width: axis === "width"
        ? panelWidthForViewport(panelWidth + direction * amount, viewport.width)
        : panelWidth,
      floatingHeight: axis === "height"
        ? panelHeightForViewport(
          floatingHeight + direction * amount,
          viewport.height,
        )
        : floatingHeight,
    });
  }

  function toggleDock() {
    commitLayoutPreference({
      ...layoutPreferenceRef.current,
      docked: !layoutPreference.docked,
      width: panelWidth,
      floatingHeight,
    });
  }

  const effectiveWelcome = pageHelp?.welcome || welcome;
  const effectiveSuggestions = pageHelp?.suggestions?.length
    ? pageHelp.suggestions
    : suggestions;

  return (
    <div
      className={[
        "justiniano",
        visible ? "is-open" : "",
        pageVariant ? "is-page" : "",
        dockActive ? "is-docked" : "",
        resizing ? "is-resizing" : "",
      ].filter(Boolean).join(" ")}
      style={pageVariant ? undefined : {
        "--justiniano-panel-width": `${panelWidth}px`,
        "--justiniano-panel-height": `${floatingHeight}px`,
      }}
    >
      {visible && (
        <section
          id={`justiniano-panel-${appId}`}
          ref={panelRef}
          className="justiniano-panel"
          role={pageVariant ? "region" : "dialog"}
          aria-label={`Conversación con Justiniano en ${appName}`}
        >
          {canResize && (
            <div
              className="justiniano-resize-handle justiniano-resize-handle--width"
              role="separator"
              tabIndex={0}
              aria-label="Cambiar el ancho de Justiniano"
              aria-orientation="vertical"
              aria-valuemin={Math.min(320, Math.floor(viewport.width * 0.5))}
              aria-valuemax={Math.floor(viewport.width * 0.5)}
              aria-valuenow={panelWidth}
              onPointerDown={(event) => beginResize("width", event)}
              onKeyDown={(event) => resizeWithKeyboard("width", event)}
            />
          )}
          {canResize && !docked && (
            <>
              <div
                className="justiniano-resize-handle justiniano-resize-handle--height"
                role="separator"
                tabIndex={0}
                aria-label="Cambiar la altura de Justiniano"
                aria-orientation="horizontal"
                aria-valuemin={Math.min(480, viewport.height - 110)}
                aria-valuemax={Math.max(1, viewport.height - 110)}
                aria-valuenow={floatingHeight}
                onPointerDown={(event) => beginResize("height", event)}
                onKeyDown={(event) => resizeWithKeyboard("height", event)}
              />
              <div
                className="justiniano-resize-handle justiniano-resize-handle--corner"
                aria-hidden="true"
                onPointerDown={(event) => beginResize("both", event)}
              />
            </>
          )}
          <header className="justiniano-header">
            <div className="justiniano-identity">
              <AssistantMark />
              <span>
                <strong>Justiniano</strong>
                <small>Asistente de {appName}</small>
              </span>
            </div>
            <div className="justiniano-header-actions">
              <button
                type="button"
                onClick={newConversation}
                aria-label="Nueva conversación"
                title="Nueva conversación"
              >
                <Plus size={18} />
              </button>
              {!pageVariant && canDock && (
                <button
                  type="button"
                  onClick={toggleDock}
                  aria-label={docked
                    ? "Desacoplar Justiniano"
                    : "Acoplar Justiniano a la derecha"}
                  title={docked
                    ? "Usar como ventana flotante"
                    : "Acoplar a la derecha"}
                >
                  {docked
                    ? <PanelRightClose size={18} />
                    : <PanelRightOpen size={18} />}
                </button>
              )}
              {!pageVariant && (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    triggerRef.current?.focus();
                  }}
                  aria-label="Cerrar Justiniano"
                >
                  <X size={19} />
                </button>
              )}
            </div>
          </header>

          <div className="justiniano-conversation" ref={scrollRef}>
            {restoring && (
              <div className="justiniano-restoring" role="status">
                <LoaderCircle className="is-spinning" size={20} />
                <span>Recuperando tu conversación…</span>
              </div>
            )}
            {!restoring && messages.length === 0 && (
              <div className="justiniano-welcome">
                <AssistantMark size={23} />
                <h2>Hola, soy Justiniano</h2>
                <p>{effectiveWelcome}</p>
                {restoreError && (
                  <p className="justiniano-restore-error">{restoreError}</p>
                )}
                {effectiveSuggestions.length > 0 && (
                  <div className="justiniano-suggestions">
                    {effectiveSuggestions.map((suggestion) => (
                      <button
                        type="button"
                        key={suggestion}
                        onClick={() => send(suggestion)}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {messages.map((message) => (
              <ConversationMessage
                key={message.id}
                message={message}
                renderers={renderers}
                context={context}
                onAction={runUiAction}
                onConfirm={confirmAction}
                onCancel={cancelAction}
              />
            ))}
          </div>

          <form
            className={[
              "justiniano-composer",
              allowAttachments ? "has-attachment-control" : "",
              draggingFiles ? "is-dragging-files" : "",
            ].filter(Boolean).join(" ")}
            onDragEnter={beginFileDrag}
            onDragOver={continueFileDrag}
            onDragLeave={endFileDrag}
            onDrop={dropFiles}
            onSubmit={(event) => {
              event.preventDefault();
              send();
            }}
          >
            {allowAttachments && attachments.length > 0 && (
              <div
                className="justiniano-active-attachments"
                aria-label="Archivos activos en la conversación"
              >
                {attachments.map((attachment) => (
                  <AttachmentChip
                    key={attachment.id}
                    attachment={attachment}
                    removable
                    onRemove={removeAttachment}
                  />
                ))}
              </div>
            )}
            {allowAttachments && (
              <>
                <input
                  ref={fileInputRef}
                  className="justiniano-file-input"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.md,.csv,.tsv,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp"
                  onChange={(event) => {
                    void uploadFiles(event.target.files);
                    event.target.value = "";
                  }}
                  disabled={
                    sending
                    || restoring
                    || uploadingAttachment
                    || attachments.length >= 5
                  }
                />
                <button
                  type="button"
                  className="justiniano-attach-button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={
                    sending
                    || restoring
                    || uploadingAttachment
                    || attachments.length >= 5
                  }
                  aria-label="Adjuntar archivos"
                  title="Adjuntar PDF, documento o imagen (máx. 10 MB)"
                >
                  {uploadingAttachment ? (
                    <LoaderCircle className="is-spinning" size={18} />
                  ) : (
                    <Paperclip size={18} />
                  )}
                </button>
              </>
            )}
            <textarea
              ref={inputRef}
              rows={1}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  send();
                }
              }}
              placeholder="Escribile a Justiniano…"
              aria-label="Mensaje para Justiniano"
              disabled={sending || restoring || uploadingAttachment}
            />
            <button
              type="submit"
              disabled={
                !draft.trim()
                || sending
                || restoring
                || uploadingAttachment
              }
              aria-label="Enviar mensaje"
            >
              {sending ? (
                <LoaderCircle className="is-spinning" size={18} />
              ) : (
                <Send size={18} />
              )}
            </button>
            {allowAttachments && attachmentError && (
              <span className="justiniano-attachment-error" role="alert">
                {attachmentError}
              </span>
            )}
            <small>
              Justiniano puede equivocarse. Verificá la información jurídica
              importante.
            </small>
          </form>
        </section>
      )}

      {!pageVariant && (
        <>
          {!visible && proactiveTip && (
            <aside className="justiniano-proactive-tip" role="status">
              <button
                className="justiniano-proactive-tip-copy"
                type="button"
                onClick={() => {
                  dismissProactiveTip();
                  setOpen(true);
                }}
              >
                <AssistantMark size={16} />
                <span>{proactiveTip.text}</span>
              </button>
              <button
                className="justiniano-proactive-tip-close"
                type="button"
                onClick={dismissProactiveTip}
                aria-label="Cerrar sugerencia de Justiniano"
              >
                <X size={14} />
              </button>
            </aside>
          )}
          <button
            ref={triggerRef}
            type="button"
            className="justiniano-trigger"
            aria-expanded={open}
            aria-controls={`justiniano-panel-${appId}`}
            aria-label={open ? "Cerrar Justiniano" : "Abrir Justiniano"}
            onClick={() => {
              if (!open) dismissProactiveTip();
              setOpen((current) => !current);
            }}
          >
            {open ? <X size={21} /> : <MessageCircle size={21} />}
            {!open && <span>Justiniano</span>}
          </button>
        </>
      )}
    </div>
  );
}
